// Token-Anchored Outro Placeholder substitute (TL-1, omo Hashline 영감 — 본 프로젝트가 이미 도입한 가장 큰 자산).
//
// 11 placeholder types (deterministic — LLM never invents numbers):
//   CHANGE / CHANGE_RATE  — buffer items _change_<field> / _changeRate_<field> 변화량 변환
//   COUNT / COUNT_NONZERO — items.length 또는 field nonzero count
//   SUM / AVG / MAX / MIN — items field aggregate + summary scalar fallback
//   TABLE_COLS            — buffer.resolvedColumns 한국어 라벨 enumeration
//   SUMMARY               — buf.summary scalar (number/string)
//
// Pattern (omo carry-over):
// - LLM은 수치를 작성하지 않고 placeholder만 사용 → hallucination 원천 차단
// - 코드가 buffer.items[]/summary에서 값을 읽어 정확한 수치로 치환
// - field 매칭: (1) items[0] keys (2) availableFields meta (3) LABEL_OVERRIDES 역방향 (한국어 라벨 → base) (4) availableFields description 역방향
// - 빈 case → _match 반환 (placeholder 원본 marker 보존) → 후처리에서 sentence/clause boundary 검출 후 surrounding 잔재 통째 drop
// - 치환 발동 시 substituted 플래그 메타 (호출자가 augmentOutro skip 판단)
//
// Post-process (P1-D2x 후속 다국어 차단):
// - Intl.Segmenter sentence boundary + 콤마 clause split → placeholder 포함 clause drop
// - 단위/종결자 enumeration X — Intl.Segmenter가 ko/en/ja sentence boundary 자동 검출
//
// [P2-1+P2-2 anchor 7] outro 직접 column enum 절 drop:
// - LLM이 {{TABLE_COLS}} placeholder 가이드 무시하고 한국어 라벨 직접 enum ("표에 수신자, 내용, 발송일가 표시돼요") 본질 차단
// - source: buffer.resolvedColumnLabels (anchor 5와 동일 meta source) — labels ≥2 콤마 enum + 동사 매칭 sentence drop
// - raw=raw label (영문 camelCase) 자동 제외 — anchor 5 RAW_CAMEL_RE 재사용
// - 정당 {{TABLE_COLS}} substitute (tableColsSubstituted flag) 시 절 보존 (회귀 risk 최소화)
// - 받침 오류 ("발송일가") 동반 케이스 자동 해결 — 절 자체 drop이므로 받침 무관

import { LABEL_OVERRIDES } from '../Config/columnLabels.js';
import { UNIT_BY_DIMENSION } from '../Config/unitsByDimension.js';
import { detectResponseLanguage } from './responseLanguage.js';
import { isAmbiguousCountKey } from './ambiguousCountKeys.js';

// [P0-1] LLM의 변형 출력(`{count}` 소문자 단일 / `{COUNT}` 대문자 단일 / `{{COUNT}}` 정상 이중) 모두 매칭.
// - 화이트리스트 type group으로 detect/clause/full 전부 통일 → 자연어 `{이름}` `{userName}` 등 false positive 완전 차단
// - case-insensitive — 소문자/대문자 변형 모두 substitute 대상
const PLACEHOLDER_TYPE_GROUP = 'CHANGE_RATE|CHANGE|COUNT_NONZERO|COUNT|SUM|AVG|MAX|MIN|SUMMARY|TABLE_COLS';
const PLACEHOLDER_DETECT_RE = new RegExp(`\\{\\{?(?:${PLACEHOLDER_TYPE_GROUP})(?::|\\}\\}?)`, 'i');
// [L3-COMPUTE-GROUP-002] capture group 3 = OPTIONAL trailing 받침-dependent josa.
//   Self-contained with its own boundary lookahead, so when the text after `}}` is NOT a valid
//   josa+boundary (e.g. `명`, `그`, `종류`, `로그`) the group matches empty and following text is untouched.
const PLACEHOLDER_FULL_RE = new RegExp(`\\{\\{?(${PLACEHOLDER_TYPE_GROUP})(?::([^}]+?))?\\}\\}?((?:은|는|이|가|을|를|과|와|으로|로)(?=[\\s.,!?)\\]]|$))?`, 'gi');
const PLACEHOLDER_CLAUSE_RE = new RegExp(`\\{\\{?(?:${PLACEHOLDER_TYPE_GROUP})(?::[^}]+)?\\}\\}?`, 'i');

// [P2-3 anchor 5] TABLE_COLS raw=raw label 검출용. backend availableFields에 한국어 라벨 부재 → resolvedColumnLabels value가 영문 camelCase key 자체로 fallback된 경우 차단.
const RAW_CAMEL_RE = /^[a-z][a-zA-Z]*[A-Z][a-zA-Z]*$/;

// [P3 anchor 12] date field value-form detect — AVG/SUM/MAX/MIN placeholder가 date field 매칭 시 parseFloat(date) 의미 오류 차단용
// (예: parseFloat("19970527")=19970527 → AVG:birthDay 평균 19,965,253 같은 큰 숫자 산출)
// pattern: YYYY-MM-DD / ISO datetime / YYYYMMDD compact (year 1900-2099) — anchor 11 AgentSession/nodeAgent와 동일
function _isDateString(s) {
  if (typeof s !== 'string') return false;
  return /^(19|20)\d{2}-\d{2}-\d{2}/.test(s)
      || /^(19|20)\d{2}\d{2}\d{2}$/.test(s);
}

// [P3 anchor 12] date field placeholder → summary key fallback 후보
// LLM이 잘못된 placeholder({{AVG:birthDay}}) 사용 시 anchor 9가 저장한 summary key({{SUMMARY:avgAge}})로 자동 redirect
// op = 'avg' | 'sum' | 'min' | 'max'
function _dateSummaryFallbackKeys(op, field) {
  const pascal = field.charAt(0).toUpperCase() + field.slice(1);
  // 1차: op + field PascalCase (anchor 9 raw key 패턴 — compute 미지정 case)
  // 2차: op + Age / Days / Years / Months (anchor 9 compute='age' 등 의미 키)
  return [`${op}${pascal}`, `${op}Age`, `${op}Days`, `${op}Years`, `${op}Months`];
}

// [P2-1+P2-2 anchor 7] LLM 직접 column enum sentence drop (placeholder 유무 무관).
// labels ≥2 콤마 enum + 동사(표시/보여/확인/정리/안내/나와/나타/노출/볼 수 있) 매칭 sentence drop.
// 받침 오류("발송일가"/"이름가") 동반 case 자동 해결.
//
// [anchor 10] 단일 label + raw key 분기 확장 — anchor 7의 ≥2 enum 조건 미달 50+건 cluster:
//   - "표에는 상품명가 표시" 21회 (받침 오류 + 단일 label)
//   - "표에는 상품명만 표시" 12회 (단일 label + 조사 "만")
//   - "표에는 이름가 표시" 9회 (받침 오류 + 단일 label)
//   - "표에는 openingHour가 표시" 6회 (raw key 단독 leak)
//   조건: 테이블 prefix(`표|차트|결과|아래 + 에(는|서는?)`) + label/raw key 단일 + 동사 매칭만 발동.
//   false positive 차단: prefix 필수 — "이름이 표시돼요" 단독 일반 문장은 보존.
// false positive 차단: verb 동시 매칭만 발동.
function _dropColumnEnumSentences(text, buffer, tableColsSubstituted) {
  if (!text || tableColsSubstituted) return text;
  const labelsRaw = Object.values(buffer?.resolvedColumnLabels || {})
    .filter(l => typeof l === 'string' && l.length >= 2);
  const koreanLabels = labelsRaw.filter(l => !RAW_CAMEL_RE.test(l));

  // raw key source — resolvedColumns / availableFields keys / resolvedColumnLabels value 중 영문 camelCase
  // (anchor 10: 한국어 라벨 부재 시 LLM이 raw key를 outro에 직접 박는 leak 차단)
  const rawKeySource = new Set();
  if (Array.isArray(buffer?.resolvedColumns)) {
    for (const c of buffer.resolvedColumns) {
      if (typeof c === 'string' && RAW_CAMEL_RE.test(c)) rawKeySource.add(c);
    }
  }
  if (buffer?.availableFields) {
    for (const k of Object.keys(buffer.availableFields)) {
      if (typeof k === 'string' && RAW_CAMEL_RE.test(k)) rawKeySource.add(k);
    }
  }
  for (const l of labelsRaw) {
    if (RAW_CAMEL_RE.test(l)) rawKeySource.add(l);
  }
  const rawKeys = [...rawKeySource];

  if (koreanLabels.length === 0 && rawKeys.length === 0) return text;

  const escape = l => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // separator: 콤마(, 、 · /) + 한국어 접속조사(과/와/이나/하고/및) — LLM이 2개 enum시 콤마 대신 conjunction 사용
  const sepRe = '(?:\\s*[,、·/]\\s*|\\s*(?:과|와|이나|하고|및)\\s*)';
  const verbTailRe = /(?:표시|보여|확인|정리|안내|나와|나타|노출|볼\s*수\s*있)/;

  // Layer A — anchor 7: ≥2 Korean labels enum + verb 매칭 sentence drop (기존)
  const enumRe = koreanLabels.length >= 2
    ? new RegExp(`(?:${koreanLabels.map(escape).join('|')})(?:${sepRe}(?:${koreanLabels.map(escape).join('|')})){1,}`)
    : null;

  // Layer B — anchor 10: 테이블 prefix + 단일 label/raw key + 동사 매칭 sentence drop
  // prefix: 표|차트|결과|아래 + 에(는|서는?) — 일반 문장 false positive 차단 핵심
  // target: koreanLabels + rawKeys 합집합 (단일 매칭 허용)
  // optional 후행 명사 (정보/내역/목록/항목) + 받침 오류 포함 josa(가/이/은/는/을/를/도/만)
  const allTargets = [...koreanLabels, ...rawKeys];
  const singleRe = allTargets.length >= 1
    ? new RegExp(`(?:표|차트|결과|아래)\\s*에(?:는|서는?)?\\s*(?:${allTargets.map(escape).join('|')})(?:\\s*(?:정보|내역|목록|항목))?\\s*(?:[가이은는을를도]|만|이나)?\\s*`)
    : null;

  const _segmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });
  const _sents = [..._segmenter.segment(text)].map(s => s.segment);
  return _sents.filter(s => {
    if (!verbTailRe.test(s)) return true;
    if (enumRe && enumRe.test(s)) return false;
    if (singleRe && singleRe.test(s)) return false;
    return true;
  }).join('');
}

// [L3-COMPUTE-GROUP-002] Korean josa (조사) correction after placeholder substitution.
//   The LLM writes a fixed josa right after a {{...}} placeholder without knowing the resolved
//   value's 받침 (final consonant). After substitution we correct it structurally from the value's
//   last Hangul syllable. Multilingual-safe: only applies when the last char is a Hangul syllable
//   (U+AC00..U+D7A3) AND a 받침-dependent josa was captured; otherwise the josa is left untouched.
const _JOSA_PAIRS = { // [withBatchim, withoutBatchim]
  '은': ['은','는'], '는': ['은','는'],
  '이': ['이','가'], '가': ['이','가'],
  '을': ['을','를'], '를': ['을','를'],
  '과': ['과','와'], '와': ['과','와'],
};
function _correctJosa(value, josa) {
  if (!value || !josa) return josa || '';
  const ch = value[value.length - 1];
  const code = ch.charCodeAt(0);
  if (code < 0xAC00 || code > 0xD7A3) return josa; // not Hangul syllable → leave as-is
  const jong = (code - 0xAC00) % 28; // 0 = no 받침
  const hasBatchim = jong !== 0;
  if (josa === '으로' || josa === '로') {
    // no 받침 OR ㄹ 받침(jong===8) → '로'; other 받침 → '으로'
    return (!hasBatchim || jong === 8) ? '로' : '으로';
  }
  const pair = _JOSA_PAIRS[josa];
  if (!pair) return josa;
  return hasBatchim ? pair[0] : pair[1];
}
function _appendJosa(value, josa) {
  if (!josa) return value;
  if (value === '' || value == null) return value; // stripped placeholder → drop orphan josa
  return `${value}${_correctJosa(String(value), josa)}`;
}

// [MULTI-SLOT SUMMARY] 동일 bare {{SUMMARY:key}}가 텍스트에 ≥2회 반복 + 그 key가 ≥2 슬롯에서 divergent → 모호 key.
//   본질: "5월 {{SUMMARY:grossRevenue}} / 6월 {{SUMMARY:grossRevenue}}"처럼 같은 placeholder를 여러 기간에 반복하면
//         단일 active 슬롯 값으로 전부 collapse (ISSUE #1). 반복(≥2회)이 collapse 신호 — 1회 인용은 active 정상 resolve(무회귀).
//   slotSummaries 미전달 / 슬롯 <2 / 1회 인용 → null (기존 동작 유지). slot-qualified는 본 검사 대상 아님.
function _computeAmbiguousSummaryKeys(text, slotSummaries) {
  if (!slotSummaries || typeof slotSummaries !== 'object') return null;
  const slotNames = Object.keys(slotSummaries);
  if (slotNames.length < 2) return null;
  const bareRe = /\{\{?SUMMARY:([A-Za-z0-9_]+)\}\}?/gi;  // bare key만 (slot-qualified는 내부 ':'로 미매칭)
  const counts = new Map();
  let m;
  while ((m = bareRe.exec(text)) !== null) {
    const k = m[1];
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const ambiguous = new Set();
  for (const [k, c] of counts) {
    if (c < 2) continue;  // 1회 인용은 collapse 위험 없음 → active resolve
    const vals = [];
    for (const sn of slotNames) {
      const s = slotSummaries[sn];
      if (s && typeof s === 'object' && k in s) vals.push(s[k]);
    }
    if (vals.length >= 2 && new Set(vals.map(v => String(v))).size >= 2) ambiguous.add(k);
  }
  return ambiguous.size ? ambiguous : null;
}

// [MULTI-SLOT SUMMARY] resolve {{SUMMARY:<key>}} (active slot) or {{SUMMARY:<slot>:<key>}} (named slot).
//   - slot-qualified ({{SUMMARY:<slot>:<key>}}): slotSummaries[slot]에서만 resolve. miss → not hit (marker 보존 → clause drop, 틀린값 X)
//   - bare key ∈ ambiguousKeys (반복+divergent) → ambiguous (marker 보존 — active 틀린값 collapse 차단)
//   - bare key 그 외(단일 슬롯/1회 인용) → active buffer summary (기존 동작 무변경 → 무회귀)
function _resolveSummaryPlaceholder(rawField, activeSummary, slotSummaries, ambiguousKeys) {
  let slot = null;
  let key = rawField;
  const ci = rawField.indexOf(':');
  if (ci > 0) {
    slot = rawField.slice(0, ci).trim();
    key = rawField.slice(ci + 1).trim();
  }
  if (!key || isAmbiguousCountKey(key)) return { hit: false, ambiguous: false, key, slot };
  if (slot) {
    const s = slotSummaries && slotSummaries[slot];
    if (s && typeof s === 'object' && key in s) return { hit: true, value: s[key], key, slot };
    return { hit: false, ambiguous: false, key, slot };  // named slot miss → marker 보존
  }
  if (ambiguousKeys && ambiguousKeys.has(key)) {
    return { hit: false, ambiguous: true, key, slot: null };  // 반복+divergent bare → marker 보존
  }
  if (activeSummary && typeof activeSummary === 'object' && key in activeSummary) {
    return { hit: true, value: activeSummary[key], key, slot: null };
  }
  return { hit: false, ambiguous: false, key, slot: null };
}

export function substitutePlaceholders(responseText, buffer, slotSummaries) {
  if (!responseText || typeof responseText !== 'string') return { text: responseText, substituted: false };
  if (!PLACEHOLDER_DETECT_RE.test(responseText)) {
    // [P2-1 anchor 7] placeholder 없어도 column enum drop은 발동 — LLM이 가이드 무시하고 직접 enum 박은 경우
    const dropped = _dropColumnEnumSentences(responseText, buffer, false);
    return { text: dropped, substituted: false };
  }
  // [MULTI-SLOT SUMMARY] 반복 bare {{SUMMARY:key}} + divergent 슬롯 → 모호 key 사전 산정 (ISSUE #1 collapse 차단)
  const _ambiguousSummaryKeys = _computeAmbiguousSummaryKeys(responseText, slotSummaries);
  // [L9a-SALES-004 fix] items 0건이어도 summary가 존재하면 SUMMARY placeholder 치환 후 나머지 strip
  // filter 0건 후 "총 환불 금액은 {{SUMMARY:totalRefundAmount}}원" 같은 응답이 빈 문자열로 strip되던 버그 수정
  if (!buffer?.items?.length) {
    const _summary = buffer?.summary;
    if (_summary && typeof _summary === 'object' && Object.keys(_summary).length > 0) {
      const _formatNum = (v) => Number.isInteger(v) ? v.toLocaleString() : parseFloat(v.toFixed(1)).toLocaleString();
      let _summarySubstituted = false;
      let _t = responseText.replace(PLACEHOLDER_FULL_RE, (_match, type, rawField, josa) => {
        const r = (() => {
          const _typeKey = (type || '').toUpperCase();
          if (_typeKey === 'SUMMARY' && rawField) {
            const _res = _resolveSummaryPlaceholder(rawField.trim(), _summary, slotSummaries, _ambiguousSummaryKeys);
            if (_res.hit) {
              const v = _res.value;
              if (typeof v === 'number' && isFinite(v)) {
                _summarySubstituted = true;
                return `${_formatNum(v)}`;
              }
              if (typeof v === 'string') {
                _summarySubstituted = true;
                return v;
              }
            }
          }
          if (_typeKey === 'COUNT' && !rawField) {
            _summarySubstituted = true;
            return '0';
          }
          return _match;
        })();
        if (r === _match) return r;        // no substitution → _match already contains the josa
        return _appendJosa(r, josa);        // substituted → re-append corrected josa
      });

      if (_summarySubstituted) {
        _t = stripPlaceholderArtifacts(_t);
        _t = _dropColumnEnumSentences(_t, buffer, false);
        return { text: _t, substituted: true };
      }
    }

    let _t = stripPlaceholderArtifacts(responseText);
    _t = _dropColumnEnumSentences(_t, buffer, false);
    return { text: _t, substituted: false };
  }

  const items = buffer.items;
  const meta = buffer.availableFields || {};
  const summary = buffer.summary || {};

  // currItem (for CHANGE/CHANGE_RATE — variance comparison context)
  const hasChange = it => it && Object.keys(it).some(k => k.startsWith('_change_') && !k.startsWith('_changeRate_'));
  const currItem = hasChange(items[items.length - 1])
    ? items[items.length - 1]
    : items.find(hasChange);

  // Resolve raw label/field → buffer base field
  const resolveBaseField = (rawField) => {
    if (!rawField) return null;
    // Direct match against items[0] keys
    if (items[0] && Object.prototype.hasOwnProperty.call(items[0], rawField)) return rawField;
    // Direct match against availableFields meta
    if (rawField in meta) return rawField;
    // CHANGE/CHANGE_RATE base via currItem
    if (currItem && (currItem[`_change_${rawField}`] != null || currItem[`_changeRate_${rawField}`] != null)) return rawField;
    // LABEL_OVERRIDES reverse (Korean label → base field)
    for (const [field, label] of Object.entries(LABEL_OVERRIDES)) {
      if (label !== rawField) continue;
      if (items[0] && Object.prototype.hasOwnProperty.call(items[0], field)) return field;
      if (field in meta) return field;
      if (currItem && (currItem[`_change_${field}`] != null || currItem[`_changeRate_${field}`] != null)) return field;
    }
    // availableFields description reverse
    for (const [field, val] of Object.entries(meta)) {
      const desc = typeof val === 'string' ? val : val?.description;
      if (desc === rawField) return field;
    }
    return null;
  };

  // Universal — backend 메타의 dimension 우선. 응답 언어에 따라 단위 매핑.
  // 메타에 dimension 없으면 빈 문자열 (placeholder는 raw number만 출력, LLM이 outro에서 단위 결정).
  // dimension enum: rate | currency | people | transaction | session | object | none
  const lang = detectResponseLanguage(responseText);
  const unitOf = (field) => {
    const m = meta[field];
    const dim = (m && typeof m === 'object') ? m.dimension : null;
    if (dim) return UNIT_BY_DIMENSION[dim]?.[lang] ?? UNIT_BY_DIMENSION[dim]?.ko ?? '';
    return '';
  };

  const getFieldValues = (field) => {
    const vals = [];
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      const v = it[field];
      if (typeof v === 'number' && isFinite(v)) vals.push(v);
    }
    return vals;
  };

  const formatNum = (v) => Number.isInteger(v) ? v.toLocaleString() : parseFloat(v.toFixed(1)).toLocaleString();

  let substituted = false;
  let tableColsSubstituted = false;  // [P2-1 anchor 7] 정당 {{TABLE_COLS}} substitute path 별도 추적 — column enum drop bypass
  const text = responseText.replace(PLACEHOLDER_FULL_RE, (_match, type, rawField, josa) => {
    rawField = rawField ? rawField.trim() : null;
    // [P0-1] case-insensitive match (gi flag) — switch는 대문자 type만 처리하므로 정규화
    const typeKey = (type || '').toUpperCase();

    // [L3-COMPUTE-GROUP-002] wrap existing switch in IIFE → re-append 받침-corrected josa on substitution
    const r = (() => {
    switch (typeKey) {
      case 'CHANGE': {
        if (!currItem || !rawField) return _match;
        const field = resolveBaseField(rawField);
        if (!field) return _match;
        const v = currItem[`_change_${field}`];
        if (typeof v !== 'number') return _match;
        substituted = true;
        const sign = v >= 0 ? '+' : '';
        return `${sign}${v.toLocaleString()}${unitOf(field)}`;
      }
      case 'CHANGE_RATE': {
        if (!currItem || !rawField) return _match;
        const field = resolveBaseField(rawField);
        if (!field) return _match;
        const v = currItem[`_changeRate_${field}`];
        if (typeof v !== 'number') return _match;
        substituted = true;
        const sign = v >= 0 ? '+' : '';
        return `(${sign}${v}%)`;
      }
      case 'COUNT': {
        // {{COUNT}} → total items, {{COUNT:field}} → nonzero count for that field
        // [L23-FILTER-FOCUS-001] capped display path — buffer._displayedCount 우선 (show_display cap 적용 시 사용자 표 row 수)
        //   본질: max_count로 sliced 표시 시 buffer.items.length는 cap 이전 (예: filter 후 173건), 사용자 표는 capped (150건)
        //         → intro에서 "총 173명" 안내 vs 표 150건 모순 (CALC_INCONSISTENT). show_display caller가 _displayedCount 전달 시 표 기준 사용.
        // [L3-COMPUTE-GROUP-002 v2] grouped buffer headline = group count (NOT entity count)
        //   본질: 사용자가 "X별 분포/구성/비율" 요청 시 headline은 group 수 (예: "회원권은 12종류"), entity 수가 아님.
        //   원래 의도 분석: "회원권별 인원 분포" → primary metric = # of types (12), secondary = total members (93).
        //   "총 12명이야" / "총 93명이야" / "총 12개 회원권" 모두 unit mismatch 또는 의미 혼동 유발.
        //   {{COUNT}} → group count로 통일 + aiUtils.js intro post-process에서 "명/건/회" → "종류" 단위 정정.
        //   entity 총수 필요 시 LLM이 {{SUMMARY:groupTotalCount}} 명시 사용 (compute group message가 가이드).
        //   감지 신호: _aggregateMetaFields에 '_count' 포함 (구조 신호 — 키워드/case ID 없음).
        if (!rawField) {
          substituted = true;
          const _isGrouped = Array.isArray(buffer?._aggregateMetaFields)
            && buffer._aggregateMetaFields.includes('_count');
          if (_isGrouped) {
            // group count = buffer.items.length (each grouped row = 1 group)
            const _groupCount = Array.isArray(buffer.items) ? buffer.items.length : 0;
            return `${_groupCount.toLocaleString()}`;
          }
          const _displayed = (buffer && typeof buffer._displayedCount === 'number')
            ? buffer._displayedCount
            : items.length;
          return `${_displayed.toLocaleString()}`;
        }
        const field = resolveBaseField(rawField);
        if (!field) return _match;
        substituted = true;
        return `${getFieldValues(field).filter(v => v !== 0).length.toLocaleString()}`;
      }
      case 'COUNT_NONZERO': {
        if (!rawField) return _match;
        const field = resolveBaseField(rawField);
        if (!field) return _match;
        substituted = true;
        return `${getFieldValues(field).filter(v => v !== 0).length.toLocaleString()}`;
      }
      // SUM/AVG/MAX/MIN/SUMMARY: raw number만 반환 (단위는 LLM 자율 — substitute가 터치 X)
      // [P3 anchor 12] AVG/SUM/MAX/MIN 4곳: date field 매칭 시 parseFloat 의미 오류 차단 + summary fallback redirect.
      //   LLM이 잘못된 placeholder ({{AVG:birthDay}}) 사용 시 anchor 9 저장 summary key({{avgAge}})로 자동 redirect.
      //   summary에도 없으면 _match 보존 → clause drop (잘못된 큰 숫자 박힘 차단).
      case 'SUM': {
        if (!rawField) return _match;
        const field = resolveBaseField(rawField);
        if (field) {
          // [anchor 12] date field detect → summary fallback redirect
          const sampleRaw = items[0]?.[field];
          if (_isDateString(sampleRaw)) {
            for (const key of _dateSummaryFallbackKeys('sum', field)) {
              if (key in summary && typeof summary[key] === 'number' && isFinite(summary[key])) {
                substituted = true;
                return `${formatNum(summary[key])}`;
              }
            }
            return _match;  // summary 없으면 marker 보존 → clause drop
          }
          const vals = getFieldValues(field);
          if (vals.length > 0) {
            const sum = vals.reduce((a, b) => a + b, 0);
            substituted = true;
            return `${sum.toLocaleString()}`;
          }
        }
        // [B2 회귀 fix — buf.summary fallback]
        if (rawField in summary) {
          const v = summary[rawField];
          if (typeof v === 'number' && isFinite(v)) {
            substituted = true;
            return `${formatNum(v)}`;
          }
        }
        return _match;
      }
      case 'AVG': {
        if (!rawField) return _match;
        const field = resolveBaseField(rawField);
        if (field) {
          // [anchor 12] date field detect → summary fallback redirect (avgAge / avgYears 등 anchor 9 key 매칭)
          const sampleRaw = items[0]?.[field];
          if (_isDateString(sampleRaw)) {
            for (const key of _dateSummaryFallbackKeys('avg', field)) {
              if (key in summary && typeof summary[key] === 'number' && isFinite(summary[key])) {
                substituted = true;
                return `${formatNum(summary[key])}`;
              }
            }
            return _match;  // summary 없으면 marker 보존 → clause drop
          }
          const vals = getFieldValues(field);
          if (vals.length > 0) {
            const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
            substituted = true;
            return `${formatNum(avg)}`;
          }
        }
        if (rawField in summary) {
          const v = summary[rawField];
          if (typeof v === 'number' && isFinite(v)) {
            substituted = true;
            return `${formatNum(v)}`;
          }
        }
        return _match;
      }
      case 'MAX': {
        if (!rawField) return _match;
        const field = resolveBaseField(rawField);
        if (field) {
          // [anchor 12] date field detect → summary fallback redirect
          const sampleRaw = items[0]?.[field];
          if (_isDateString(sampleRaw)) {
            for (const key of _dateSummaryFallbackKeys('max', field)) {
              if (key in summary && typeof summary[key] === 'number' && isFinite(summary[key])) {
                substituted = true;
                return `${formatNum(summary[key])}`;
              }
            }
            return _match;
          }
          const vals = getFieldValues(field);
          if (vals.length > 0) {
            substituted = true;
            return `${Math.max(...vals).toLocaleString()}`;
          }
        }
        if (rawField in summary) {
          const v = summary[rawField];
          if (typeof v === 'number' && isFinite(v)) {
            substituted = true;
            return `${formatNum(v)}`;
          }
        }
        return _match;
      }
      case 'MIN': {
        if (!rawField) return _match;
        const field = resolveBaseField(rawField);
        if (field) {
          // [anchor 12] date field detect → summary fallback redirect
          const sampleRaw = items[0]?.[field];
          if (_isDateString(sampleRaw)) {
            for (const key of _dateSummaryFallbackKeys('min', field)) {
              if (key in summary && typeof summary[key] === 'number' && isFinite(summary[key])) {
                substituted = true;
                return `${formatNum(summary[key])}`;
              }
            }
            return _match;
          }
          const vals = getFieldValues(field);
          if (vals.length > 0) {
            substituted = true;
            return `${Math.min(...vals).toLocaleString()}`;
          }
        }
        if (rawField in summary) {
          const v = summary[rawField];
          if (typeof v === 'number' && isFinite(v)) {
            substituted = true;
            return `${formatNum(v)}`;
          }
        }
        return _match;
      }
      case 'TABLE_COLS': {
        // [P1-D3] outro/finish의 표 컬럼 enumeration용 — show_display 후 buffer에 저장된 displayed cols 한국어 라벨 콤마 join
        // LLM이 자체 작성한 컬럼 enumeration("표에 X, Y, Z 정리되어 있어요")의 거짓 차단
        // [P2-3 anchor 5] 한국어 라벨이 없거나 raw=raw (영문 camelCase value) 인 컬럼은 enum에서 제외 — backend availableFields 한국어 라벨 부재 시 raw key (예: "openingHour", "sections") outro 노출 본질 차단
        const cols = buffer.resolvedColumns;
        const labels = buffer.resolvedColumnLabels || {};
        if (!Array.isArray(cols) || cols.length === 0) return _match;
        const out = cols
          .map(c => {
            const lbl = labels[c];
            if (!lbl || lbl === c || RAW_CAMEL_RE.test(lbl)) return null;
            return lbl;
          })
          .filter(Boolean);
        if (out.length === 0) return _match;  // 모든 컬럼 raw → placeholder 보존 → cleanup이 clause drop
        substituted = true;
        tableColsSubstituted = true;  // [P2-1 anchor 7] 정당 substitute path 마크 — post-process column enum drop 우회
        return out.join(', ');
      }
      case 'SUMMARY': {
        if (!rawField) return _match;
        // [MULTI-SLOT] slot-qualified {{SUMMARY:<slot>:<key>}} 또는 bare {{SUMMARY:<key>}} resolve.
        //   bare + 멀티슬롯 divergent → ambiguous → marker 보존 (active 틀린값 collapse 차단, ISSUE #1).
        //   generic count key 차단은 헬퍼가 key 기준으로 수행.
        const _sumRes = _resolveSummaryPlaceholder(rawField, summary, slotSummaries, _ambiguousSummaryKeys);
        if (!_sumRes.hit) return _match;  // miss / named-slot 부재 / ambiguous → marker 보존 → clause drop
        const v = _sumRes.value;
        if (typeof v === 'number' && isFinite(v)) {
          // [PLACEHOLDER-SUMMARY-SCOPE] top-N scope 가드 — bare(active) 슬롯 값에만 적용 (slot-qualified는 명시 슬롯이라 scope 무관).
          //   본질: 명시적 top-N(상위 N명만) 요청 시 display는 cap(N)인데 summary scalar는 full scope(예: 285명 합계) → mismatch.
          //   해소: _userExplicitTopNSatisfied + scope > displayed 시 substitute skip → marker 보존 → 후처리 clause drop.
          if (!_sumRes.slot) {
            const _displayedForScope = (typeof buffer?._displayedCount === 'number')
              ? buffer._displayedCount
              : items.length;
            if (buffer?._userExplicitTopNSatisfied
                && typeof buffer._scopeTotal === 'number'
                && buffer._scopeTotal > _displayedForScope) {
              return _match;  // marker 보존 → clause drop
            }
          }
          substituted = true;
          return `${formatNum(v)}`;
        }
        if (typeof v === 'string') {
          substituted = true;
          return v;
        }
        return _match;
      }
      default:
        // placeholder type 자체가 invalid — marker로 보존할 의미 없음, 빈 string으로 strip
        return '';
    }
    })();
    if (r === _match) return r;        // no substitution → _match already contains the josa, preserved
    return _appendJosa(r, josa);        // substituted → re-append 받침-corrected josa
  });

  // [P1-D2 후속] substitute 빈 case는 placeholder를 marker로 보존 → 다국어 sentence boundary(Intl.Segmenter) + 콤마 clause split 후 placeholder 포함 clause drop
  // 동작: "이번 주 예약 현황이에요. {{SUMMARY:totalCount}}건입니다 1. 박은정..." → "이번 주 예약 현황이에요. 박은정..." (placeholder clause drop)
  let cleanedRaw = text;
  if (PLACEHOLDER_DETECT_RE.test(cleanedRaw)) {
    const _segmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });
    const _sentences = [..._segmenter.segment(cleanedRaw)].map(s => s.segment);
    // [천단위 구분자 보호] 콤마/세미콜론 직후 숫자가 오면 thousands separator(예: 32,166,500)이므로 절 경계 아님 → 미분리.
    //   (?!\d): substitute된 toLocaleString 숫자가 미해소 placeholder clause-drop 시 중간에서 잘리는 것 차단.
    const _clauses = _sentences.flatMap(s => s.split(/(?<=[,;])(?!\d)\s*/));
    cleanedRaw = _clauses.filter(c => !PLACEHOLDER_CLAUSE_RE.test(c)).join('');
  }

  // [P2-1+P2-2 anchor 7] LLM이 {{TABLE_COLS}} 가이드 무시하고 한국어 라벨 직접 enum 시 sentence drop
  // 정당 substitute (tableColsSubstituted=true) 시 bypass
  cleanedRaw = _dropColumnEnumSentences(cleanedRaw, buffer, tableColsSubstituted);
  const cleaned = cleanedRaw
    .replace(/\(\s*\)/g, '')
    .replace(/\(\s*(\([^()]+\))\s*\)/g, '$1')  // ((...))→(...) collapse double parens
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/^[\s,.;]+/, '')
    .replace(/[\s,.;]+$/, '')
    .trim();
  return { text: cleaned, substituted };
}

// Clause-level drop of any clause containing a placeholder marker (TL-1 migration from aiUtils.js).
// Used by AgentSession/nodeAgent intro/outro post-cleanup when substitute path leaves residual markers
// (e.g. buffer.items empty + LLM still emitted {{...}}).
export function stripPlaceholderArtifacts(text) {
  if (!text || typeof text !== 'string') return text;
  if (!PLACEHOLDER_DETECT_RE.test(text)) return text;
  const segments = text.split(/(?<=[,.!?\n])/);
  const filtered = segments.filter(s => !PLACEHOLDER_CLAUSE_RE.test(s));
  return filtered.join('')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/^[\s,.;]+/, '')
    .replace(/[\s,.;]+$/, '')
    .trim();
}
